import React, { createContext, useReducer, useContext, ReactNode } from 'react';
import { Product } from '../types/product';
import { isOutOfStock } from '../utils/products';

export interface CartItem extends Product {
  quantity: number;
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  shippingCost: number;
  bulkDiscount: number;
  discountedTotal: number;
  totalWithShipping: number;
}

type CartActionType =
  | { type: 'ADD_TO_CART'; payload: { product: Product; quantity: number } }
  | { type: 'REMOVE_FROM_CART'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' };

const SHIPPING_NORMAL = 7.45;
const SHIPPING_TESTERPACK = 2.80;
const BULK_DISCOUNT_THRESHOLD = 3;
const BULK_DISCOUNT_PERCENT = 0.17;

const isTesterpack = (item: CartItem): boolean => {
  return item.name.toLowerCase().includes('tester');
};

const initialState: CartState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
  shippingCost: 7.45,
  bulkDiscount: 0,
  discountedTotal: 0,
  totalWithShipping: 0
};

const cartReducer = (state: CartState, action: CartActionType): CartState => {
  const calculateTotals = (items: CartItem[]) => {
    const totalPrice = items.reduce((total, item) => total + (item.price * item.quantity), 0);
    const totalItems = items.reduce((total, item) => total + item.quantity, 0);

    // Testerpack-only shipping: €2.80 if only 1 testerpack in cart
    const isOnlyTesterpack = items.length === 1 && isTesterpack(items[0]) && items[0].quantity === 1;
    const shippingCost = items.length > 0 ? (isOnlyTesterpack ? SHIPPING_TESTERPACK : SHIPPING_NORMAL) : 0;

    // 17% bulk discount when 3+ total items
    const bulkDiscount = totalItems >= BULK_DISCOUNT_THRESHOLD
      ? Math.round(totalPrice * BULK_DISCOUNT_PERCENT * 100) / 100
      : 0;

    const discountedTotal = totalPrice - bulkDiscount;
    const totalWithShipping = discountedTotal + shippingCost;

    return {
      items,
      totalItems,
      totalPrice,
      shippingCost,
      bulkDiscount,
      discountedTotal,
      totalWithShipping
    };
  };

  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItemIndex = state.items.findIndex(
        item => item.id === action.payload.product.id
      );

      let updatedItems: CartItem[];
      if (existingItemIndex > -1) {
        updatedItems = state.items.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + action.payload.quantity }
            : item
        );
      } else {
        updatedItems = [
          ...state.items,
          { ...action.payload.product, quantity: action.payload.quantity }
        ];
      }

      return calculateTotals(updatedItems);
    }
    case 'REMOVE_FROM_CART': {
      const filteredItems = state.items.filter(item => item.id !== action.payload);

      return calculateTotals(filteredItems);
    }
    case 'UPDATE_QUANTITY': {
      const quantityUpdatedItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: action.payload.quantity }
          : item
      );

      return calculateTotals(quantityUpdatedItems);
    }
    case 'CLEAR_CART': {
      return initialState;
    }
    default:
      return state;
  }
};

const CartContext = createContext<{
  state: CartState;
  dispatch: React.Dispatch<CartActionType>;
} | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  return (
    <CartContext.Provider value={{ state, dispatch }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }

  const { state, dispatch } = context;

  const addToCart = (product: Product, quantity: number = 1) => {
    // Prevent adding out-of-stock products to cart
    if (isOutOfStock(product)) {
      console.warn('Cannot add out-of-stock product to cart:', product.name);
      return;
    }
    dispatch({
      type: 'ADD_TO_CART',
      payload: { product, quantity }
    });
  };

  const removeFromCart = (itemId: string) => {
    dispatch({
      type: 'REMOVE_FROM_CART',
      payload: itemId
    });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { id: itemId, quantity }
    });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  return {
    cart: state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart
  };
}; 